import groupBy from 'lodash/groupBy';

import type { MarketGroupData } from 'app/pages/MatchPage/types';
import {
  PLAYER_MARKET_CATEGORY,
  PREBUILT_STORYBUILDER_TEMPLATE,
  TIME_WINDOW_MARKET_CATEGORY_PART
} from 'app/constants/markets';
import { formatOrdinals } from 'app/i18n/utils';

import { isTruthy, assertNever } from './types';
import { getOpponentFromPlayerId } from './match';
import { parseIntOrNull, isNumber } from './numbers';
import { partitionArray } from './array';
import { isPandascoreVideogame } from './videogame';
import { marketGroupsDef } from './markets/market-groups';
import { formatGameTimestamp } from './date';

function calculateProbability(format: OddsFormat, probability: number) {
  const cappedProbability = probability < 1 ? probability : 1;

  switch (format) {
    case 'probability':
      return cappedProbability;
    case 'decimal':
      return 1 / cappedProbability;
    case 'american': {
      const decimal = 1 / cappedProbability;
      if (decimal < 2) {
        // negative value
        return -100 / (decimal - 1);
      }

      // positive value
      return (decimal - 1) * 100;
    }
    default:
      assertNever(format);
  }
}

function formatOdds(
  probability: number,
  format: OddsFormat,
  displayedOverround: number | null,
  competititorValue: boolean
): number | null {
  if (probability === 0) {
    return null;
  }

  if (displayedOverround !== null && !competititorValue) {
    const overroundApplyedToProba = probability + displayedOverround / 100;

    return calculateProbability(format, overroundApplyedToProba);
  }

  return calculateProbability(format, probability);
}

function formatDisplayedOdds(
  probability: number,
  format: OddsFormat,
  displayedOverround: number | null,
  competititorValue: boolean
): string {
  const value = formatOdds(probability, format, displayedOverround, competititorValue);

  if (value === null) {
    return '-';
  }

  switch (format) {
    case 'probability':
      return value.toFixed(3);
    case 'decimal':
      return value.toFixed(2);
    case 'american': {
      const rounded = Math.round(value);
      if (rounded > 0) {
        return `+${rounded.toString()}`;
      }
      return rounded.toString();
    }
    default:
      assertNever(format);
  }
}

function computeSelectionDiff<
  T extends { selections: Pick<Outcome, 'probability' | 'probability_with_overround'>[] }
>(market: T, key: 'probability_with_overround' | 'probability' = 'probability'): number {
  const probabilities = market.selections.map(selection => selection[key] ?? 0);
  const highestValue = Math.max(...probabilities);
  const lowestValue = Math.min(...probabilities);

  return highestValue - lowestValue;
}

function getMostBalancedMarket(markets: Market[]): Market | null {
  return markets.reduce((result, market) => {
    // Cedric: for round-winner-2-way there is a case where there are no market.enabled=true. This is
    // expected and for that we instead take the higher round index as the most balanced market
    if (market.template == 'round-winner-2-way') {
      if (!result) {
        return market;
      }

      if (market.round_index && result.round_index && market.round_index > result.round_index) {
        return market;
      }

      return result;
    }

    if (!market.enabled) {
      return result;
    }
    if (!result) {
      return market;
    }

    if (computeSelectionDiff(market) < computeSelectionDiff(result)) {
      return market;
    }
    return result;
  }, null as Market | null);
}

/**
 * A market block group markets by:
 * - event_type
 * - event_id
 * - template
 * - participant_type
 * - participant_id
 * - line (only if dynamic_line === false)
 */
const getMarketGroupKey =
  (rosters?: PandapiOpponents, includeDynamicLine = false, useDynamicLineGroupingKey = true) =>
  ({
    dynamic_line,
    category,
    event_type,
    event_id,
    template,
    participant_type,
    participant_id,
    line,
    is_micromarket,
    half_index,
    quarter_index,
    dynamic_line_grouping_key,
    name,
    time_window_start,
    time_window_end
  }: MarketBase): string => {
    if (category?.includes(TIME_WINDOW_MARKET_CATEGORY_PART)) {
      if (dynamic_line_grouping_key && useDynamicLineGroupingKey) {
        return `${dynamic_line_grouping_key}-${time_window_start}-${time_window_end}`;
      }

      return `${event_type}-${event_id}-${template}-${participant_type}-${participant_id}-${line}-${time_window_start}-${time_window_end}`;
    }

    if (dynamic_line_grouping_key && useDynamicLineGroupingKey) {
      return dynamic_line_grouping_key;
    }

    if (template === PREBUILT_STORYBUILDER_TEMPLATE) {
      return `${event_type}-${event_id}-${template}-${name}`;
    }

    if (
      category === PLAYER_MARKET_CATEGORY &&
      participant_type === 'Player' &&
      rosters &&
      rosters.opponents &&
      rosters.opponent_type === 'Team'
    ) {
      const opponent = getOpponentFromPlayerId(participant_id, rosters.opponents);

      return `${event_type}-${event_id}-${template}-${participant_type}-${
        opponent?.id ?? ''
      }-${participant_id}-${half_index ?? quarter_index ?? null}`;
    }

    if (is_micromarket) {
      return `micromarket-${event_type}-${event_id}-${template}-${participant_type}-${participant_id}-${line}`;
    }

    if (!includeDynamicLine && dynamic_line) {
      return `${event_type}-${event_id}-${template}-${participant_type}-${participant_id}-${
        half_index ?? quarter_index ?? null
      }`;
    }

    return `${event_type}-${event_id}-${template}-${participant_type}-${participant_id}-${line}-${
      half_index ?? quarter_index ?? null
    }`;
  };

function compareLines(lineA: string | null, lineB: string | null): number {
  // we put null line at the end
  if (lineA === null) return 1;
  if (lineB == null) return -1;

  const parsedLineA = parseFloat(lineA);
  const parsedLineB = parseFloat(lineB);
  // if lines are numbers, we use number comparison instead of string comparison
  // to avoid "3" > "10"
  if (!isNaN(parsedLineA) && !isNaN(parsedLineB)) {
    return parsedLineA - parsedLineB;
  }

  if (lineA > lineB) return 1;
  if (lineA < lineB) return -1;
  return 0;
}

function sortMarketLines(markets: Market[]): Market[] {
  const newList = [...markets];
  newList.sort((marketA, marketB) => {
    const lineA: string | null = marketA.line;
    const lineB: string | null = marketB.line;

    return compareLines(lineA, lineB);
  });

  return newList;
}

function groupMarkets(input: Market[], rosters?: PandapiOpponents): MarketBlock[] {
  const groups = groupBy(input, getMarketGroupKey(rosters));

  return Object.keys(groups)
    .map<MarketBlock | null>(key => {
      const markets = groups[key];
      const sortedMarkets = sortMarketLines(markets);
      const enabledMarkets = sortedMarkets.filter(
        market => market.template === 'round-winner-2-way' || market.enabled
      );
      const disabledMarkets = sortedMarkets.filter(
        market => market.template !== 'round-winner-2-way' && !market.enabled
      );
      const mostBalancedMarket = getMostBalancedMarket(enabledMarkets);
      const reviewed = enabledMarkets.every(market => market.reviewed);

      if (!mostBalancedMarket) {
        return null;
      }

      return {
        id: key,
        reviewed,
        category: mostBalancedMarket.category,
        dynamicLine: mostBalancedMarket.dynamic_line,
        mostBalancedMarket,
        enabledMarkets,
        disabledMarkets,
        updatedAt: mostBalancedMarket.updated_at
      };
    })
    .filter(isTruthy);
}

function getMatchMarketBlocks(
  matchMarkets: MatchMarketsBase,
  pandascoreMarkets?: MatchMarketsBase,
  rosters?: PandapiOpponents
): MatchMarketBlocks {
  const pandascoreMarketsKeys = groupBy(
    pandascoreMarkets?.markets ?? [],
    getMarketGroupKey(rosters, true)
  );

  const matchMarketWithPandacoreMarket = matchMarkets.markets.map(market => {
    const key = getMarketGroupKey(rosters, true)(market);
    return {
      ...market,
      pandascore_market: pandascoreMarketsKeys[key]?.[0] ?? null
    };
  });

  const marketsBlocks = groupMarkets(matchMarketWithPandacoreMarket);
  const games = matchMarkets.games.map(game => {
    const { markets } = game;

    const partitionnedMarkets = partitionArray(markets, market =>
      market.is_micromarket ? 'micromarkets' : 'markets'
    ) as { micromarkets: MicroMarket[]; markets: MarketBase[] };

    const pandascoreGameMarketsKeys = groupBy(
      pandascoreMarkets?.games.find(psGame => psGame.position === game.position)?.markets ?? [],
      getMarketGroupKey(rosters, true)
    );

    const gameMarketWithPandacoreMarket = game.markets.map(market => {
      const key = getMarketGroupKey(rosters, true)(market);
      return {
        ...market,
        pandascore_market: pandascoreGameMarketsKeys[key]?.[0] ?? null
      };
    });

    const blocks = groupMarkets(gameMarketWithPandacoreMarket, rosters);
    const gameBlock: GameMarketBlock = {
      ...game,
      market_blocks: blocks
    };
    if (partitionnedMarkets.micromarkets?.length) {
      gameBlock.micromarkets = partitionnedMarkets.micromarkets;
    }
    return gameBlock;
  });

  return {
    ...matchMarkets,
    market_blocks: marketsBlocks,
    games
  };
}

function findMarket(markets: MatchMarketBlocks, id: string): MarketBlock | undefined {
  const matchMarketBlock = markets.market_blocks.find(marketBlock => marketBlock.id === id);

  if (matchMarketBlock) {
    return matchMarketBlock;
  }

  for (let i = 0, l = markets.games.length; i < l; i += 1) {
    const gameMarketBlock = markets.games[i].market_blocks.find(
      marketBlock => marketBlock.id === id
    );
    if (gameMarketBlock) {
      return gameMarketBlock;
    }
  }
}

function isOutcomeResulted(result: OutcomeResult | null): boolean {
  return result !== null && result !== 'non_resulted';
}

function getParticipantName(
  participantId: number | null,
  opponents: PandapiOpponent[]
): '?' | string {
  if (!participantId) {
    return '?';
  }

  const participant = opponents.find(({ opponent }) => opponent.id === participantId);
  if (!participant) {
    return '?';
  }

  const acronym = participant.type === 'Team' ? participant.opponent.acronym : null;
  return acronym ?? participant.opponent.name;
}

const getPlayerName = (playerId: number | null, rosters: PandapiOpponents | undefined) => {
  if (rosters && rosters.opponent_type === 'Team') {
    return (
      rosters.opponents
        .map(opponent => opponent.players)
        .flat()
        .find(player => player.id === playerId)?.name ?? ''
    );
  }

  return '';
};

type MarketForHelper = Pick<
  Market,
  | 'template'
  | 'participant_id'
  | 'line'
  | 'dynamic_line'
  | 'category'
  | 'name'
  | 'player_away_id'
  | 'player_home_id'
  | 'selections'
  | 'quarter_index'
  | 'half_index'
  | 'team_id'
  | 'player_id'
  | 'is_prebuilt'
  | 'time_window_end'
  | 'time_window_start'
>;

const isPrebuiltMarket = (market: MarketForHelper): market is PrebuiltMarket => market.is_prebuilt;

const getLineValue = (market: MarketForHelper) => {
  if (isPrebuiltMarket(market)) {
    return market.player_kills?.toString() || market.line || 'TBD';
  }

  // there are some markets with line value but isn't dynamic
  // we format market name with line value only when the market has dynamic line
  return market.line && !market.dynamic_line ? `${market.line}` : '';
};

function formatMarketName(
  market: MarketForHelper,
  match: Pick<PandapiMatch, 'opponents'>,
  rosters: PandapiOpponents | undefined,
  marketNames: Record<string, string>
): string {
  const {
    template,
    participant_id,
    category,
    name,
    player_away_id,
    player_home_id,
    selections,
    quarter_index,
    half_index
  } = market;
  // Special for player market
  if (category === PLAYER_MARKET_CATEGORY || template === PREBUILT_STORYBUILDER_TEMPLATE) {
    return name.replace('#', '').trim();
  }
  const lineValue = getLineValue(market);
  const participantName = participant_id ? getParticipantName(participant_id, match.opponents) : '';

  const translatedTemplate = marketNames[template];

  const teamName = isPrebuiltMarket(market)
    ? getParticipantName(market.team_id, match.opponents)
    : '';
  const playerName = isPrebuiltMarket(market) ? getPlayerName(market.player_id, rosters) : '';

  // Specific to player most kill matchup
  if (player_away_id && player_home_id) {
    const playerAName = selections[0].name;
    const playerBName = selections[1].name;

    return translatedTemplate
      .replace('[ParticipantA]', playerAName)
      .replace('[ParticipantB]', playerBName)
      .trim();
  }

  if (category === 'micro') {
    return translatedTemplate;
  }

  return translatedTemplate
    .replace('[Line]', lineValue)
    .replace('[Participant]', participantName)
    .replace('[Half]', () =>
      half_index !== undefined ? `${formatOrdinals(half_index)} Half` : '[Half]'
    )
    .replace('[Quarter]', () =>
      quarter_index !== undefined ? `${formatOrdinals(quarter_index)} Quarter` : '[Quarter]'
    )
    .replace('[Line-Goal]', () =>
      parseIntOrNull(lineValue) ? `${formatOrdinals(parseInt(lineValue))}` : lineValue
    )
    .replace('[Team]', teamName)
    .replace('[Player]', playerName)
    .replace('[TimeWindowStart]', formatGameTimestamp(market.time_window_start ?? 0))
    .replace('[TimeWindowEnd]', formatGameTimestamp(market.time_window_end ?? 0))
    .trim();
}

function formatHandicapOutcome<
  O extends Pick<Outcome, 'name' | 'position'>,
  M extends Pick<Market, 'line'>
>(outcomeName: string, { position }: O, { line }: M): string {
  if (!line) {
    return outcomeName;
  }

  const positive = !line.startsWith('-');
  const lineValue = positive ? line : line.substring(1);

  switch (position) {
    case 0: {
      const sign = positive ? '+' : '-';
      return `${outcomeName} ${sign}${lineValue}`;
    }
    case 1: {
      // second outcome has opposite sign of market line
      const sign = positive ? '-' : '+';
      return `${outcomeName} ${sign}${lineValue}`;
    }
    default:
      // should not happend because there are 2 opponents
      return outcomeName;
  }
}

const formatOutcomeLine = (line: string | null, marketTemplate: string): string => {
  if (marketTemplate === 'timer-over-under') {
    return line ? formatGameTimestamp(parseInt(line)) : '';
  }

  return line ?? '';
};

function formatOutcomeName<
  M extends Pick<Market, 'template' | 'line' | 'dynamic_line' | 'is_micromarket'>,
  O extends Pick<
    Outcome,
    | 'name'
    | 'template'
    | 'participant_id'
    | 'score_home'
    | 'score_away'
    | 'range_max'
    | 'range_min'
    | 'number'
    | 'position'
  >
>(
  outcome: O,
  market: M,
  opponents: PandapiOpponent[],
  outcomeNames: Record<string, string>
): string {
  const { name } = outcome;
  const { template, line, dynamic_line } = market;
  const computedParticipantName = getParticipantName(outcome.participant_id, opponents);
  const participantName = ['?', undefined, null, ''].includes(computedParticipantName)
    ? outcome.name
    : computedParticipantName;

  const [opponent, _] = opponents.filter(
    opponent => opponent.opponent.id !== outcome.participant_id
  );
  const opponentName = opponent ? getParticipantName(opponent.opponent.id, opponents) : '';

  const translatedTemplate = outcomeNames[outcome.template] ?? name;

  let scoreArray = null;
  if (isNumber(outcome?.score_home) && isNumber(outcome?.score_away)) {
    scoreArray =
      opponents.findIndex(({ opponent }) => outcome.participant_id === opponent.id) === 0
        ? [outcome.score_home, outcome.score_away]
        : [outcome.score_away, outcome.score_home];
  }
  if (isNumber(outcome.range_max) && isNumber(outcome.range_min)) {
    scoreArray = [outcome.range_min, outcome.range_max];
  }

  const outcomeName =
    translatedTemplate
      .replaceAll('[Participant]', participantName)
      .replaceAll('[Opponent]', opponentName)
      .replace('[Score]', scoreArray ? scoreArray.join('-') : '')
      .replace('[Range]', scoreArray ? scoreArray.join('-') : '')
      .replace('[Number]', outcome?.number?.toString() ?? '') || name;

  if (template.includes('handicap')) {
    return formatHandicapOutcome(outcomeName, outcome, market);
  }
  if (line && dynamic_line && !market.is_micromarket) {
    const formatedLine = formatOutcomeLine(line, template);
    return `${outcomeName} ${formatedLine}`;
  }

  return outcomeName;
}

const hasEventSuffix = (marketBlock: MarketBlock): boolean =>
  marketGroupsDef[0].mainSlugs.includes(marketBlock.mostBalancedMarket.template);

function getEventSuffix(market: Market): string {
  if (market.event_type === 'match') {
    return 'MATCH';
  }
  return 'GAME';
}

function formatMarket<T extends Market>(
  market: T,
  match: PandapiMatch,
  rosters: PandapiOpponents | undefined,
  withEventSuffix: boolean,
  marketNames: Record<string, string>,
  outcomeNames: Record<string, string>
): T {
  const marketName = formatMarketName(market, match, rosters, marketNames);
  const name = withEventSuffix ? `${marketName} - ${getEventSuffix(market)}` : marketName;
  const selections: Outcome[] = market.selections.map(outcome => {
    const name = formatOutcomeName(outcome, market, match.opponents, outcomeNames);
    return { ...outcome, name };
  });

  return {
    ...market,
    name,
    selections
  };
}

function areTraderInputsDisplayed(
  userType: UserType,
  hasTraderInputs: boolean,
  videogame: VideogameSlug,
  inputs_enable: boolean | undefined,
  category: string
): boolean {
  switch (userType) {
    case 'pandascore':
      return isPandascoreVideogame(videogame);
    case 'trial':
      return false;
    case 'bookmaker': {
      return hasTraderInputs && !!inputs_enable && category === 'winner';
    }
    default:
      assertNever(userType);
  }
}

const isAllMarketReviewed = (group: MarketGroupData) => {
  if (
    group.markets.every(market => market.reviewed) &&
    group.mainMarkets.every(market => market.reviewed)
  ) {
    return true;
  }

  return false;
};

const isNoneMarketReviewed = (group: MarketGroupData | undefined) => {
  if (
    group &&
    group.markets.every(market => !market.reviewed) &&
    group.mainMarkets.every(market => !market.reviewed)
  ) {
    return true;
  }

  return false;
};

const getDisplayedMarket = (market: string): MatchPreviewDisplayedMarket => {
  if (market.includes('over-under')) {
    return 'map-over-under';
  }
  if (market.includes('handicap')) {
    return 'map-handicap';
  }
  return 'winner';
};

function getResultLabel(result: OutcomeResult | null, t: (key: string) => string): string {
  switch (result) {
    case 'win':
      return t('match_market-win');
    case 'lose':
      return t('match_market-lose');
    case 'void':
      return t('match_market-void');
    case 'non_resulted':
      return t('match_market-non_resulted');
    case null:
      return '';
  }
}

const canActivateMarket = (marketTemplate: string, match: PandapiMatch): boolean => {
  if (marketTemplate === 'round-winner-2-way') {
    return match.betting_metadata?.traded_with_scraper || false;
  }
  return true;
};

export {
  formatOdds,
  formatDisplayedOdds,
  compareLines,
  sortMarketLines,
  groupMarkets,
  getMatchMarketBlocks,
  findMarket,
  isOutcomeResulted,
  formatMarket,
  getMostBalancedMarket,
  areTraderInputsDisplayed,
  isAllMarketReviewed,
  isNoneMarketReviewed,
  formatHandicapOutcome,
  hasEventSuffix,
  computeSelectionDiff,
  formatOutcomeName,
  getDisplayedMarket,
  formatMarketName,
  isPrebuiltMarket,
  getResultLabel,
  getMarketGroupKey,
  formatOutcomeLine,
  canActivateMarket
};
